import { useEffect, useRef } from 'react';
import { animate } from 'animejs';
import Navbar from '../components/Navbar';
import BackgroundPattern from '../components/BackgroundPattern';
import PortfolioSection from '../components/PortfolioSection';
import AboutSection from '../components/AboutSection';
// import LeaveReviewSection from '../components/LeaveReviewSection';
import ContactInfoSection from '../components/ContactInfoSection';
import Footer from '../components/Footer';
import ConnectSection from '../components/ConnectSection';
import '../App.css';

const Home = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const processRef = useRef(null);

  useEffect(() => {
    const initHeroAnimations = () => {
      animate('.hero-tagline', { translateY: [50, 0], scale: [0.8, 1], opacity: [0, 1], rotateX: [90, 0], duration: 1200, delay: 300, easing: 'spring(1, 80, 10, 0)' });
      setTimeout(() => {
        animate('.hero-headline', { translateY: [60, 0], scale: [0.8, 1], opacity: [0, 1], duration: 1200, easing: 'easeOutBack(1.7)', complete: () => {
          animate('.hero-headline', { scale: [1, 1.02, 1], duration: 4000, direction: 'alternate', loop: true, easing: 'easeInOutSine' });
        }});
      }, 800);
      setTimeout(() => {
        animate('.hero-subtitle', { translateY: [60, 0], scale: [0.9, 1], opacity: [0, 1], letterSpacing: ['0.5em', '0em'], duration: 1200, easing: 'easeOutBack(1.7)' });
      }, 1800);
      setTimeout(() => {
        animate('.hero-cta', { opacity: [0, 1], translateY: [50, 0], duration: 800, easing: 'easeOutExpo', complete: () => {
          animate('.hero-cta a', { scale: [0.8, 1], opacity: [0.7, 1], duration: 600, delay: (el, i) => 200 * i, easing: 'easeOutBack(1.7)' });
        }});
      }, 2200);
      setTimeout(() => {
        animate('.hero-stats', { opacity: [0, 1], translateY: [60, 0], duration: 1000, easing: 'easeOutExpo', complete: () => {
          animate('.hero-stats > div', { scale: [0.8, 1], opacity: [0.7, 1], translateY: [30, 0], duration: 600, delay: (el, i) => 100 * i, easing: 'easeOutBack(1.7)', complete: () => {
            animate('.hero-stats > div', { scale: [1, 1.02, 1], duration: 3000, direction: 'alternate', loop: true, easing: 'easeInOutSine', delay: (el, i) => 800 * i });
          }});
        }});
      }, 2800);
    };

    const initServicesAnimations = () => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate('#services h2', { translateY: [50, 0], scale: [0.8, 1], opacity: [0, 1], rotateX: [90, 0], duration: 800, easing: 'easeOutBack(1.7)' });
            animate('.service-card', { scale: [0.3, 1], opacity: [0, 1], translateY: [100, 0], rotateY: [180, 0], duration: 1000, delay: (el, i) => 300 * i, easing: 'easeOutBack(2)', complete: () => {
              animate('.service-card', { translateY: [0, -10, 0], duration: 3000, direction: 'alternate', loop: true, easing: 'easeInOutSine', delay: (el, i) => 1000 * i });
            }});
            animate('.service-icon', { scale: [0, 1.3, 1], opacity: [0, 1], rotateZ: [360, 0], duration: 1200, delay: (el, i) => 600 + (300 * i), easing: 'easeOutElastic(1, .6)', complete: () => {
              animate('.service-icon', { scale: [1, 1.1, 1], duration: 2000, direction: 'alternate', loop: true, easing: 'easeInOutQuad', delay: (el, i) => 800 * i });
            }});
          }
        });
      }, { threshold: 0.2 });
      if (servicesRef.current) observer.observe(servicesRef.current);
    };

    const initProcessAnimations = () => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate('#process h2', { translateY: [50, 0], scale: [0.8, 1], opacity: [0, 1], rotateX: [90, 0], duration: 800, easing: 'easeOutBack(1.7)' });
            animate('.process-step', { opacity: [0, 1], translateY: [120, 0], scale: [0.5, 1], rotateX: [180, 0], duration: 1000, delay: (el, i) => 400 * i, easing: 'easeOutBack(2)', complete: () => {
              animate('.process-step .w-12', { scale: [1, 1.2, 1], rotateZ: [0, 360, 0], duration: 800, delay: (el, i) => 200 * i, easing: 'easeOutElastic(1, .8)' });
              animate('.process-step', { translateY: [0, -5, 0], duration: 4000, direction: 'alternate', loop: true, easing: 'easeInOutSine', delay: (el, i) => 1500 * i });
            }});
          }
        });
      }, { threshold: 0.3 });
      if (processRef.current) observer.observe(processRef.current);
    };

    const initFinalCTAAnimations = () => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate('#contact h2', { translateY: [60, 0], scale: [0.8, 1], opacity: [0, 1], rotateX: [90, 0], duration: 1000, easing: 'easeOutBack(1.7)' });
            animate('#contact p', { translateY: [40, 0], opacity: [0, 1], duration: 800, delay: 300, easing: 'easeOutExpo' });
            animate('.cta-button', { translateY: [80, 0], scale: [0.5, 1], opacity: [0, 1], rotateZ: [180, 0], duration: 1200, delay: 600, easing: 'easeOutElastic(1, .8)', complete: () => {
              animate('.cta-button', { scale: [1, 1.05, 1], rotateZ: [0, 2, -2, 0], duration: 3000, direction: 'alternate', loop: true, easing: 'easeInOutSine' });
            }});
          }
        });
      }, { threshold: 0.4 });
      const ctaSection = document.querySelector('#contact');
      if (ctaSection) observer.observe(ctaSection);
    };

    setTimeout(initHeroAnimations, 100);
    initServicesAnimations();
    initProcessAnimations();
    initFinalCTAAnimations();
  }, []);

  const smoothScrollTo = (event, selector) => {
    event.preventDefault();
    const target = document.querySelector(selector);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (selector.startsWith('#')) {
      const updatedPath = `${window.location.pathname}${selector}`;
      window.history.replaceState(null, '', updatedPath);
    }
  };

  return (
    <div className="min-h-screen text-white overflow-hidden relative" style={{ backgroundColor: '#0a0a1a' }}>
      <BackgroundPattern />
      <Navbar />

      <section id="home" ref={heroRef} className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 z-10">
        <div className="max-w-7xl mx-auto text-center px-4">
          <div className="hero-tagline flex items-center mt-20 mb-8 justify-center opacity-0">
            <span className="text-indigo-300 text-sm sm:text-base font-small">⚡ Powered by Advanced AI Technology</span>
          </div>
          <h1 className="hero-headline text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-8 bg-gradient-to-r from-blue-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent leading-tight">
            Intelligent Solutions,
            <br />
            Real-World Results
          </h1>
          <p className="hero-subtitle text-lg sm:text-xl md:text-2xl mb-12 text-gray-300 max-w-5xl mx-auto opacity-0 leading-relaxed">
            Transform your business with cutting-edge AI solutions, custom development, and intelligent automation. We deliver technology that drives measurable results and competitive advantage.
          </p>
          <div className="hero-cta flex flex-col sm:flex-row gap-4 justify-center mb-20 opacity-0">
            <a
              href="/#portfolio"
              onClick={(event) => smoothScrollTo(event, '#portfolio')}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 min-w-48"
            >
              View Our Projects
            </a>
            <a href="/#services" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 min-w-48">
              Learn More
            </a>
            <a
              href="/#connect"
              onClick={(event) => {
                event.preventDefault();
                const target = document.querySelector('#connect');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                window.dispatchEvent(new Event('open-connect'));
              }}
              className="bg-transparent border-2 border-gray-600 hover:border-white text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 min-w-48"
            >
              Get Started Today
            </a>
          </div>
          <div className="hero-stats grid grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 max-w-5xl mx-auto opacity-0">
            {/* <div className="text-center p-4 rounded-lg bg-gray-800/30 backdrop-blur-sm border border-gray-700/30">
              <div className="flex items-center justify-center mb-3">
                <span className="text-indigo-400 mr-2 text-lg">✓</span>
                <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">50+</span>
              </div>
              <p className="text-xs sm:text-sm text-gray-400 font-medium">Projects Deployed</p>
            </div> */}
            <div className="text-center p-4 rounded-lg bg-gray-800/30 backdrop-blur-sm border border-gray-700/30">
              <div className="flex items-center justify-center mb-3">
                <span className="text-green-400 mr-2 text-lg">●</span>
                <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">99.9%</span>
              </div>
              <p className="text-xs sm:text-sm text-gray-400 font-medium">System Uptime</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-gray-800/30 backdrop-blur-sm border border-gray-700/30">
              <div className="flex items-center justify-center mb-3">
                <span className="text-yellow-400 mr-2 text-lg">☀</span>
                <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">24/7</span>
              </div>
              <p className="text-xs sm:text-sm text-gray-400 font-medium">Expert Support</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-gray-800/30 backdrop-blur-sm border border-gray-700/30">
              <div className="flex items-center justify-center mb-3">
                <span className="text-purple-400 mr-2 text-lg">⚡</span>
                <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">10x</span>
              </div>
              <p className="text-xs sm:text-sm text-gray-400 font-medium">Faster Results</p>
            </div>
          </div>
        </div>
      </section>

      <section id="services" ref={servicesRef} className="relative py-20 px-6 z-10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">What We Do</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="service-card bg-gray-800 p-8 rounded-lg text-center opacity-0">
              <div className="service-icon w-16 h-16 mx-auto mb-6 bg-indigo-600 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Website Development</h3>
              <p className="text-gray-300">We build fast, secure, and SEO-friendly websites with modern tech, clean code, and accessible design that convert visitors into customers. We build everything from custom e-commerce solutions to professional business portfolio websites.</p>
            </div>
            <div className="service-card bg-gray-800 p-8 rounded-lg text-center opacity-0">
              <div className="service-icon w-16 h-16 mx-auto mb-6 bg-indigo-600 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">App Development</h3>
              <p className="text-gray-300">We craft cross‑platform web and mobile apps with robust architecture, smooth UX, and scalable APIs tailored to your business. We create user-friendly and powerful mobile applications for both Android & iOS.</p>
            </div>
            <div className="service-card bg-gray-800 p-8 rounded-lg text-center opacity-0">
              <div className="service-icon w-16 h-16 mx-auto mb-6 bg-indigo-600 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Website Maintenance</h3>
              <p className="text-gray-300">Keep your site healthy with updates, security patches, performance tuning, monitoring, and automated backups.</p>
            </div>
          </div>
        </div>
      </section>



      <section id="process" ref={processRef} className="relative py-20 px-6 z-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="process-step text-center opacity-0">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-bold mb-3">Discover & Plan</h3>
              <p className="text-gray-300">We start with a conversation to understand your business and your goals. No tech jargon, just a clear plan.</p>
            </div>
            <div className="process-step text-center opacity-0">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-bold mb-3">Build & Refine</h3>
              <p className="text-gray-300">Our team gets to work, building your solution with regular updates and your feedback at every stage.</p>
            </div>
            <div className="process-step text-center opacity-0">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-bold mb-3">Launch & Support</h3>
              <p className="text-gray-300">We launch your project and provide ongoing support to ensure it continues to deliver results for your business.</p>
            </div>
          </div>
        </div>
      </section>

      {/* <VideoSection /> */}

      <PortfolioSection />
      
      {/* <TestimonialsSection /> */}

      <AboutSection />
      {/* <LeaveReviewSection /> */}

      <ContactInfoSection />

      <section id="contact" className="relative py-20 px-6 z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Ready to unlock your business's potential?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">Let's build something great together. Schedule a free, no-obligation consultation to see how our tech solutions can work for you.</p>
          <a href="#connect" onClick={() => window.dispatchEvent(new Event('open-connect'))} className="cta-button relative inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-all duration-300 overflow-hidden">
            <span className="relative z-10 flex items-center justify-center">
              Schedule My Free Call
              <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </a>
        </div>
      </section>

      <ConnectSection />

      <Footer />
    </div>
  );
};

export default Home;
